/**
 * Pure helpers for the /draft flow.
 *
 * SRP: pure data transforms. No React, no Office.js, no fetches.
 * Every branch is unit-testable with plain input literals.
 *
 * # HISTORY: Spec 29 Phase A (2026-04-23) introduced this module
 * #   for the CONVERSATIONAL /draft chip picker — Asthra asked the
 * #   writer "what are you drafting?" then "which library?", and
 * #   the writer answered via inline chips. Phase 6 (2026-04-26)
 * #   retired chip-picker selection per the rule "library +
 * #   doctype each have ONE canonical surface" (LibraryHub for
 * #   library, DoctypePicker for doctype). The chip-builder /
 * #   prompt-copy helpers were retired with that change; only the
 * #   selection-synthesis helper survives because it's still the
 * #   shell's hand-off point from picker to run.
 *
 * What lives here now:
 *   - ``DoctypeChoice``: typed selection payload the picker emits.
 *   - ``AskingLibraryContext`` + ``buildLoomFreshSelection``: the
 *     pure synthesis from (doctype, library, workspace) into the
 *     LoomFreshSelection the run pipeline accepts.
 */

import type { DocDomain } from "./doc-catalog";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Exactly what the shell needs to construct a ``LoomFreshSelection``
 *  once both picks land. Narrow on purpose — YAGNI. */
export interface DoctypeChoice {
  docCode: string;
  backendDocType: string;
  docFramework: string | null;
  docName: string;
  domain: DocDomain;
  /** Optional regulatory-authority chip (e.g. "ICH", "MDR"). */
  auth: string | null;
  /** Where the submission is filed — a key of ``SUBMISSION_REGIONS``. Picked
   *  by the writer in the DoctypePicker's region step; required to draft. */
  region?: string;
}

/** Submission regions offered when the server's ``/doc-types`` list has none
 *  (same codes as ``csr_sim.prompts.drafting_context.SUBMISSION_REGIONS``). */
export const SUBMISSION_REGIONS: Readonly<Record<string, string>> = {
  US: "United States — FDA",
  EU: "European Union — EMA / notified bodies",
  JP: "Japan — PMDA",
  UK: "United Kingdom — MHRA",
  CA: "Canada — Health Canada",
  AU: "Australia — TGA",
  IN: "India — CDSCO",
  SG: "Singapore — HSA",
  CH: "Switzerland — Swissmedic",
  GLOBAL: "Multiple regions",
};

// ---------------------------------------------------------------------------
// Selection synthesis — pure
// ---------------------------------------------------------------------------

/** Subset of the asking-library state shape needed to synthesise a
 *  LoomFreshSelection. Decoupled from the shell's full DraftFlowStep
 *  union so this helper stays pure + portable. */
export interface AskingLibraryContext {
  docCode: string;
  backendDocType: string;
  docFramework: string | null;
  docName: string;
  region?: string | null;
}

/** Inputs to the LoomFreshSelection synthesis: the doctype context
 *  the writer already picked, the library they just selected, and
 *  the active workspace. Pure: same inputs → same output, no I/O. */
export function buildLoomFreshSelection(
  doctype: AskingLibraryContext,
  library: { id: string; name: string },
  workspaceId: string | null,
): {
  workspaceId: string | null;
  libraryId: string;
  libraryName: string;
  docCode: string;
  docName: string;
  docFramework?: string;
  backendDocType: string;
  folderId?: string | null;
  region?: string;
} {
  return {
    workspaceId,
    libraryId: library.id,
    libraryName: library.name,
    docCode: doctype.docCode,
    docName: doctype.docName,
    docFramework: doctype.docFramework ?? undefined,
    backendDocType: doctype.backendDocType,
    folderId: null,
    region: doctype.region ?? undefined,
  };
}

/** Build the committed selection for a custom (build-on-existing-doc) run whose
 *  writer SKIPPED the source library. The library fields are intentionally
 *  empty — the audit library is later bootstrapped from the document's own
 *  content — while the doctype fields keep the doctype chip resolved. Pure: the
 *  shell hands the already-resolved doc name/framework in. ``docCode`` doubles
 *  as ``backendDocType`` because a custom import carries the backend stem in
 *  ``RunLoadContext.docType``. */
export function buildCustomSkipSelection(args: {
  docCode: string;
  docName: string;
  docFramework?: string | null;
  workspaceId: string | null;
}): {
  workspaceId: string | null;
  libraryId: string;
  libraryName: string;
  docCode: string;
  docName: string;
  docFramework?: string;
  backendDocType: string;
} {
  return {
    workspaceId: args.workspaceId,
    libraryId: "",
    libraryName: "",
    docCode: args.docCode,
    docName: args.docName,
    docFramework: args.docFramework ?? undefined,
    backendDocType: args.docCode,
  };
}

/** The name a new draft is offered before the writer types one: what they
 *  picked plus today's date, e.g. "CSR · US · 17 Oct". */
export function suggestedDraftName(selection: { docCode: string; region?: string | null }, now = new Date()): string {
  const date = now.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return [selection.docCode, selection.region, date].filter(Boolean).join(" · ");
}

/** The section picker's folder choice for a new draft: Unfiled plus the
 *  workspace's folders, preselected to ``folderId``. Offered as soon as the
 *  folders have loaded, even when there are none yet, so a writer can always
 *  see where the draft goes and start their first folder ("+ New folder…").
 *  ``undefined`` (no folder row) only while loading or when they failed to load. */
export function pickerFolderChoice(
  folders: ReadonlyArray<{ id: string; label: string }> | undefined,
  folderId?: string | null,
): { options: Array<{ value: string; label: string }>; initial: string } | undefined {
  if (!folders) return undefined;
  return {
    options: [{ value: "", label: "Unfiled" }, ...folders.map((f) => ({ value: f.id, label: f.label }))],
    initial: folderId && folders.some((f) => f.id === folderId) ? folderId : "",
  };
}
